import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import type { Lead } from '../../services/api/leads';
import { formatMonthYearShort } from '../../utils/dateFormat';
import { LEGACY_CZ_NUMBER_LOCALE, numberFormatter } from '../../utils/localeFormat';

interface RevenueTimelineProps {
  leads: Lead[];
  exchangeRates?: Map<string, number>;
  onLeadClick: (lead: Lead) => void;
}

function formatCZK(value: number): string {
  return numberFormatter({
    style: 'currency',
    currency: 'CZK',
    maximumFractionDigits: 0,
  }, LEGACY_CZ_NUMBER_LOCALE).format(value);
}

function addMonths(dateStr: string, months: number): Date {
  const d = new Date(dateStr);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1));
}

function monthKey(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key: string): string {
  // MKS-351 — "Oct 2026" from the shared util; `key` is already `YYYY-MM`.
  return formatMonthYearShort(key);
}

const COLORS = [
  '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
];

export default function RevenueTimeline({ leads, exchangeRates, onLeadClick }: RevenueTimelineProps) {
  const { chartData, dealKeys } = useMemo(() => {
    const wonLeads = leads.filter(
      // MKS-345 — reads the stage's kind, not its name.
      (l) => l.stageKind === 'won' && l.dealValue && parseFloat(l.dealValue) > 0 && l.expectedStartDate
    );

    if (wonLeads.length === 0) return { chartData: [], dealKeys: [] };

    // Build unique deal keys and per-deal monthly distributions
    const dealKeys: { key: string; name: string }[] = [];
    // Map: deal key -> Map<monthKey, amount>
    const dealMonthly = new Map<string, Map<string, number>>();
    const allMonths = new Set<string>();

    for (const lead of wonLeads) {
      const dealKey = `deal_${lead.id}`;
      dealKeys.push({ key: dealKey, name: lead.customer?.name || lead.name });

      const rawValue = parseFloat(lead.dealValue!);
      const currency = lead.dealValueCurrency ?? 'CZK';
      const rate = currency !== 'CZK' && exchangeRates ? (exchangeRates.get(currency) ?? 1) : 1;
      const totalValue = currency === 'CZK' ? rawValue : rawValue * rate;
      // Work a month, invoice at month end, payment arrives paymentDays later
      // Net 30 = +1 month shift, Net 60 = +2 months, etc.
      const paymentDays = lead.paymentTerm?.days ?? 30;
      const monthShift = Math.ceil(paymentDays / 30);

      if (lead.expectedEndDate) {
        // First compute project months, then shift each by payment terms
        const projectStart = new Date(lead.expectedStartDate!);
        const projectEnd = new Date(lead.expectedEndDate);
        const months: string[] = [];
        const cursor = new Date(Date.UTC(projectStart.getUTCFullYear(), projectStart.getUTCMonth(), 1));
        const endMonth = new Date(Date.UTC(projectEnd.getUTCFullYear(), projectEnd.getUTCMonth(), 1));
        while (cursor <= endMonth) {
          const shifted = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + monthShift, 1));
          months.push(monthKey(shifted));
          cursor.setUTCMonth(cursor.getUTCMonth() + 1);
        }
        if (months.length === 0) months.push(monthKey(addMonths(lead.expectedStartDate!, monthShift)));

        // Distribute evenly across the revenue months
        const perMonth = totalValue / months.length;

        const monthMap = new Map<string, number>();
        for (const m of months) {
          monthMap.set(m, perMonth);
          allMonths.add(m);
        }
        dealMonthly.set(dealKey, monthMap);
      } else {
        // No end date — lump sum shifted by payment terms
        const m = monthKey(addMonths(lead.expectedStartDate!, monthShift));
        allMonths.add(m);
        dealMonthly.set(dealKey, new Map([[m, totalValue]]));
      }
    }

    // Sort months and fill gaps (use UTC to avoid DST issues)
    const sortedMonths = [...allMonths].sort();
    if (sortedMonths.length === 0) return { chartData: [], dealKeys: [] };

    const [startY, startM] = sortedMonths[0]!.split('-').map(Number) as [number, number];
    const [endY, endM] = sortedMonths[sortedMonths.length - 1]!.split('-').map(Number) as [number, number];
    const monthList: string[] = [];

    let y = startY, m = startM;
    while (y < endY || (y === endY && m <= endM)) {
      monthList.push(`${y}-${String(m).padStart(2, '0')}`);
      m++;
      if (m > 12) { m = 1; y++; }
    }

    // Build chart data
    const chartData = monthList.map((m) => {
      const row: Record<string, unknown> = { month: m, label: monthLabel(m) };
      let total = 0;
      for (const deal of dealKeys) {
        const val = dealMonthly.get(deal.key)?.get(m) ?? 0;
        row[deal.key] = val;
        total += val;
      }
      row._total = total;
      // Transparent anchor segment stacked on top of every bar so the total
      // label always renders at the true top of the stack, regardless of which
      // deals are present in a given month.
      row._labelAnchor = 0;
      return row;
    });

    return { chartData, dealKeys };
  }, [leads, exchangeRates]);

  if (chartData.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Expected Revenue Timeline</h2>
      <p className="text-sm text-gray-500 mb-4">
        Won deals distributed proportionally across expected project duration
      </p>
      <ResponsiveContainer width="100%" height={300}>
        {/* Key the chart on the set of stacked series so it fully remounts when
            the filter adds/removes deals. Recharts otherwise keeps stale stack
            offsets and the total label anchors mid-stack until a page refresh. */}
        <BarChart key={dealKeys.map((d) => d.key).join('|')} data={chartData} margin={{ top: 20, right: 20, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12, fill: '#6b7280' }}
          />
          <YAxis
            tickFormatter={(v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)}
            tick={{ fontSize: 12, fill: '#6b7280' }}
          />
          <Tooltip
            cursor={false}
            formatter={(value, name) => {
              const deal = dealKeys.find((d) => d.key === name);
              return [formatCZK(Number(value)), deal?.name ?? String(name)];
            }}
            labelFormatter={(label) => String(label)}
            contentStyle={{ fontSize: 13, borderRadius: 8 }}
            itemSorter={(item) => -(Number(item.value) || 0)}
          />
          <Legend
            formatter={(value) => {
              const deal = dealKeys.find((d) => d.key === value);
              return deal?.name ?? value;
            }}
            wrapperStyle={{ fontSize: 12 }}
          />
          {dealKeys.map((deal, i) => (
            <Bar
              key={deal.key}
              dataKey={deal.key}
              stackId="revenue"
              fill={COLORS[i % COLORS.length]}
              maxBarSize={60}
              cursor="pointer"
              onClick={() => {
                const leadId = parseInt(deal.key.replace('deal_', ''), 10);
                const lead = leads.find((l) => l.id === leadId);
                if (lead) onLeadClick(lead);
              }}
            />
          ))}
          {/* Recharts 3 drops zero-height rectangles (Bar.computeBarRectangles),
              which would strip this anchor and its total LabelList. A custom
              (empty) shape sets hasCustomShape, keeping the zero-height segment
              alive so the total renders at the true top of the stack. */}
          <Bar dataKey="_labelAnchor" stackId="revenue" fill="transparent" maxBarSize={60} isAnimationActive={false} legendType="none" tooltipType="none" shape={() => <g />}>
            <LabelList
              dataKey="_total"
              position="top"
              formatter={(v) => { const n = Number(v); return n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}k` : String(Math.round(n)); }}
              style={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
